import React from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { STANDARD_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { LazySuspense } from '@atlassian/react-loosely-lazy/suspense/component';
import { fg } from '@atlassian/jira-feature-gating';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import type { JSMGracePeriodBanner } from './JSMGracePeriodBanner.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const JSMGracePeriodBannerLazy = lazyAfterPaint<typeof JSMGracePeriodBanner>(
	() =>
		import(
			/* webpackChunkName: "async-jsm-grace-period-banner" */ './JSMGracePeriodBanner.tsx'
		).then((module) => module.JSMGracePeriodBanner),
	{ ssr: false },
);

const JSMGracePeriodBannerAsyncInner = (): React.JSX.Element => {
	return (
		<JSErrorBoundary id="async.jsm-grace-period-banner" teamName="Sequoia" fallback="unmount">
			{fg('jsm-queues-ttai-batch-1') ? (
				// FLASH-961: Replace Placeholder (which creates a UFO hold blocking TTAI) with
				// LazySuspense (no UFO hold). The banner is non-critical — it only shows for
				// tenants in a billing grace period. All JSM users on JSM pages incur the hold
				// when jsm_gp_announcements_banner gate is ON, even if they never see the banner.
				<LazySuspense fallback={null}>
					<JSMGracePeriodBannerLazy />
				</LazySuspense>
			) : (
				<Placeholder name="async-jsm-grace-period-banner" fallback={null}>
					<JSMGracePeriodBannerLazy />
				</Placeholder>
			)}
		</JSErrorBoundary>
	);
};

const JSMGracePeriodBannerAsyncGated = (): React.ReactNode => {
	const accountId = useAccountId();
	const isSiteAdmin = useIsSiteAdmin();
	const appEditions = useAppEditions();
	// The banner is only shown to Standard JSM site admins (see `getGpEndDateData`,
	// which returns no data for anyone else). Hoisting that eligibility check here
	// lets us skip downloading the banner chunk and running its hooks/resources for
	// ineligible users, instead of loading everything just to render `null`.
	if (appEditions.serviceDesk !== STANDARD_EDITION || !accountId || !isSiteAdmin) {
		return null;
	}
	return <JSMGracePeriodBannerAsyncInner />;
};

export const JSMGracePeriodBannerAsync = (): React.ReactNode => {
	if (fg('perf-jsm-issue-view-ttai-grace-period')) {
		return <JSMGracePeriodBannerAsyncGated />;
	}
	return <JSMGracePeriodBannerAsyncInner />;
};
